import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Info, X, Loader2, RefreshCcw, Lightbulb } from "lucide-react";
import { toast } from "sonner";

import { Button } from "./ui/button";
import { motorRelatedSuggestions } from "../lib/api";
import JobCardDetailModal from "./JobCardDetailModal";

function fmtPrecio(n) {
  if (n == null) return "";
  return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(n);
}

function tituloCorto(texto, max = 110) {
  if (!texto) return "";
  const primera = texto.split(".")[0]?.trim() ?? "";
  return primera.slice(0, max);
}

function tituloDedupKey(texto) {
  if (!texto) return "";
  const primera = texto.split(".")[0] ?? "";
  return primera
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function cantidadDefecto(unidad) {
  const u = (unidad ?? "").trim().toLowerCase();
  if (["m²", "m2"].includes(u)) return 10;
  if (["m", "ml"].includes(u)) return 5;
  if (["m³", "m3"].includes(u)) return 1;
  if (u === "kg") return 10;
  if (u === "l") return 5;
  return 1;
}

const FUENTE_LABEL = {
  llm_complemento: "Sugerencia experta",
  hermano_taxonomia: "Mismo capítulo CYPE",
};

export default function RelatedSuggestionsPanel({
  contextoPrompt,
  intencionesActuales,
  excludeJobcardIds,
  proyectoIntegral,
  onAdd,
  loading,
  disabled,
}) {
  const [sugerencias, setSugerencias] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [descartadas, setDescartadas] = useState(() => new Set());
  const [yaUsadas, setYaUsadas] = useState(() => new Set());
  const [detalle, setDetalle] = useState(null);
  const [solicitado, setSolicitado] = useState(false);

  const intencionesPrincipalesKey = useMemo(
    () =>
      (intencionesActuales ?? [])
        .filter(
          (i) => !(typeof i.area === "string" && i.area.startsWith("extra_")),
        )
        .map((i) => `${i.area}:${i.jobcard_id ?? ""}:${i.cantidad ?? ""}`)
        .join("|"),
    [intencionesActuales],
  );

  const fetchAbortRef = useRef(null);

  const cargar = async () => {
    if (disabled) return;
    if (!intencionesActuales || intencionesActuales.length === 0) {
      setSugerencias([]);
      return;
    }
    setCargando(true);
    setError(null);
    setSolicitado(true);
    if (fetchAbortRef.current) fetchAbortRef.current.cancelado = true;
    const ctrl = { cancelado: false };
    fetchAbortRef.current = ctrl;
    const exclude = Array.from(
      new Set([
        ...(excludeJobcardIds ?? []),
        ...descartadas,
        ...yaUsadas,
      ]),
    );
    try {
      const data = await motorRelatedSuggestions({
        contextoPrompt,
        intencionesActuales,
        excludeJobcardIds: exclude,
        topK: 8,
        proyectoIntegral: proyectoIntegral ?? null,
      });
      if (ctrl.cancelado) return;
      const recibidas = data?.sugerencias ?? [];
      const filtradas = recibidas.filter(
        (s) => !descartadas.has(s.jobcard_id) && !yaUsadas.has(s.jobcard_id),
      );
      setSugerencias((prev) => {
        const dedupe = new Map();
        const tituloMap = new Map();
        for (const s of [...prev, ...filtradas]) {
          if (descartadas.has(s.jobcard_id) || yaUsadas.has(s.jobcard_id)) continue;
          const idKey = (s.jobcard_id ?? "").toUpperCase();
          if (!idKey) continue;
          const tituloKey = tituloDedupKey(s.candidata?.texto ?? s.titulo);
          if (dedupe.has(idKey)) continue;
          if (tituloKey && tituloMap.has(tituloKey)) continue;
          dedupe.set(idKey, s);
          if (tituloKey) tituloMap.set(tituloKey, idKey);
        }
        return Array.from(dedupe.values()).sort((a, b) => b.score - a.score);
      });
    } catch (e) {
      if (ctrl.cancelado) return;
      console.error(e);
      const msg = e.response?.data?.message || e.message || "error";
      setError(msg);
      toast.error(`Sugerencias IA: ${msg}`);
    } finally {
      if (!ctrl.cancelado) setCargando(false);
    }
  };

  useEffect(() => {
    if (disabled) return;
    if (!intencionesPrincipalesKey) return;
    const t = setTimeout(() => {
      cargar();
    }, 1200);
    return () => clearTimeout(t);
  }, [intencionesPrincipalesKey, disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnadir = async (s) => {
    const candidata = s.candidata;
    if (!candidata) return;
    const cantidad = cantidadDefecto(candidata.unidad);
    try {
      await onAdd({ jobcard: candidata, cantidad });
      setYaUsadas((prev) => {
        const next = new Set(prev);
        next.add(s.jobcard_id);
        return next;
      });
    } catch (e) {
      console.error(e);
    }
  };

  const externos = useMemo(() => {
    const set = new Set();
    for (const id of excludeJobcardIds ?? []) set.add(id);
    return set;
  }, [excludeJobcardIds]);

  const visibles = sugerencias.filter(
    (s) =>
      !descartadas.has(s.jobcard_id) &&
      !yaUsadas.has(s.jobcard_id) &&
      !externos.has(s.jobcard_id),
  );

  return (
    <div className="rx-card border border-blue-100 bg-gradient-to-br from-blue-50/40 to-white p-5" data-testid="related-suggestions">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-gradient-to-br from-blue-500 to-purple-500 text-white">
            <Lightbulb className="w-4 h-4" />
          </span>
          <div>
            <div className="font-display font-bold text-sm text-slate-800 leading-tight">
              Partidas que un experto añadiría
            </div>
            <div className="text-[11px] text-slate-500">
              Sugerencias generadas por IA a partir de tu presupuesto actual y el catálogo CYPE.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {cargando && (
            <span className="inline-flex items-center gap-1 text-[11px] text-blue-600 font-semibold">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando…
            </span>
          )}
          {!cargando && visibles.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={cargar}
              disabled={disabled}
              className="gap-2 border-slate-300"
              data-testid="related-refresh-btn"
            >
              <RefreshCcw className="w-3.5 h-3.5" />
              Volver a buscar
            </Button>
          )}
        </div>
      </div>

      {cargando && visibles.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
          Analizando presupuesto y consultando catálogo…
        </div>
      )}

      {error && !cargando && (
        <div className="text-sm bg-red-50 border border-red-200 rounded-md p-3 text-red-700">
          {error}
        </div>
      )}

      {solicitado && !cargando && visibles.length === 0 && !error && (
        <div className="text-sm text-slate-500 py-3">
          La IA no encontró partidas complementarias relevantes ahora mismo.
        </div>
      )}

      {visibles.length > 0 && (
        <ul className="space-y-2 mt-1">
          {visibles.map((s) => {
            const candidata = s.candidata ?? {};
            const titulo = tituloCorto(candidata.texto ?? s.titulo);
            const precio = candidata.coste_total;
            const unidad = candidata.unidad ?? "ud";
            const fuenteLabel = FUENTE_LABEL[s.fuente] ?? s.fuente;
            return (
              <li
                key={s.jobcard_id}
                className="bg-white border border-slate-200 rounded-md p-3 flex items-start gap-3 hover:border-blue-300 transition-colors"
                data-testid={`related-suggestion-${s.jobcard_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-blue-600">
                      {fuenteLabel}
                    </span>
                    {candidata.familia && (
                      <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                        · {candidata.familia}
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-semibold text-slate-800 leading-snug truncate">
                    {titulo}
                  </div>
                  <div className="text-xs text-slate-500 leading-snug mt-0.5 line-clamp-2">
                    {s.razon}
                  </div>
                  {precio != null && (
                    <div className="text-xs text-slate-600 mt-1 tabular-nums">
                      <span className="font-semibold text-blue-700">
                        {fmtPrecio(precio)} €
                      </span>
                      <span className="text-slate-400"> /{unidad}</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => handleAnadir(s)}
                    disabled={disabled || loading}
                    className="gap-1 bg-blue-600 hover:bg-blue-700 h-8 px-3 text-xs"
                    data-testid={`related-add-${s.jobcard_id}`}
                  >
                    <Plus className="w-3.5 h-3.5" /> Añadir
                  </Button>
                  <button
                    type="button"
                    onClick={() => setDetalle(candidata)}
                    className="inline-flex items-center justify-center gap-1 text-[11px] text-slate-500 hover:text-blue-600"
                    data-testid={`related-info-${s.jobcard_id}`}
                  >
                    <Info className="w-3 h-3" /> Ver detalle
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setDescartadas((prev) => {
                        const next = new Set(prev);
                        next.add(s.jobcard_id);
                        return next;
                      })
                    }
                    className="inline-flex items-center justify-center gap-1 text-[11px] text-slate-400 hover:text-red-600"
                    data-testid={`related-discard-${s.jobcard_id}`}
                  >
                    <X className="w-3 h-3" /> Descartar
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <JobCardDetailModal
        open={!!detalle}
        onClose={() => setDetalle(null)}
        jobcard={detalle}
      />
    </div>
  );
}
